import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import SettingsSubNavMobile from "@/components/settings/settings-sub-nav-mobile"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  brandSettingsCandidatesPath,
  brandSettingsCharterPath,
  brandSettingsIntegrationsPath,
  brandSettingsInvitesPath,
  brandSettingsLinkSharingPath,
  brandSettingsProductsPath,
  brandSettingsProfilePath,
  brandSettingsProgressionPath,
  brandSettingsRewardsPath,
  brandSettingsTeamPath,
  brandSettingsThresholdsPath,
} from "@/routes"

/**
 * OKS-21 / OKS-40 / OKS-305 — Vertical sub-nav for the Brand Settings
 * shell.
 *
 * V1 sub-pages (locked):
 *   - Profile          (OKS-40)
 *   - Link sharing     (OKS-305 — between Profile and Crew Charter per
 *                       UX Spec §2.1 / TRD K12; CTPO may re-order at PR)
 *   - Crew Charter     (OKS-40)
 *   - Thresholds       (OKS-40)
 *   - Invites          (OKS-21)
 *   - Team             (OKS-672 — internal tab at /brand/settings/team)
 *
 * Active-state styling matches the existing user-settings layout.
 * `aria-current="page"` for screen readers.
 *
 * On `< md`, an alternate `<Select>`-based mobile nav renders via
 * `<SettingsSubNavMobile>` and the desktop list hides — UX Spec §5.
 */

interface NavItem {
  title: string
  href: string
  testId: string
  external?: boolean
}

export function BrandSettingsNav() {
  const { t } = useTranslation()
  const { url } = usePage()

  const items: readonly NavItem[] = [
    {
      title: t("settings.brand.nav.profile"),
      href: brandSettingsProfilePath(),
      testId: "brand-settings-nav-profile",
    },
    {
      // OKS-305 — Sits between Profile (brand identity) and Charter
      // (creator-facing rules) per UX Spec §2.1.
      title: t("settings.brand.nav.link_sharing"),
      href: brandSettingsLinkSharingPath(),
      testId: "brand-settings-nav-link-sharing",
    },
    {
      title: t("settings.brand.nav.charter"),
      href: brandSettingsCharterPath(),
      testId: "brand-settings-nav-charter",
    },
    {
      title: t("settings.brand.nav.thresholds"),
      href: brandSettingsThresholdsPath(),
      testId: "brand-settings-nav-thresholds",
    },
    {
      // OKS-456 — reward defaults (XP + coins) every mission inherits.
      title: t("settings.brand.nav.rewards"),
      href: brandSettingsRewardsPath(),
      testId: "brand-settings-nav-rewards",
    },
    {
      title: t("settings.brand.nav.progression"),
      href: brandSettingsProgressionPath(),
      testId: "brand-settings-nav-progression",
    },
    {
      title: t("settings.brand.nav.invites"),
      href: brandSettingsInvitesPath(),
      testId: "brand-settings-nav-invites",
    },
    {
      // OKS-950 — Candidat·e·s, DIRECTLY after Invites (UX Spec §2). Both
      // govern how someone gets into the crew, so the adjacency makes the
      // recruit → validate → invite ordering legible in the nav itself.
      title: t("settings.brand.nav.candidates"),
      href: brandSettingsCandidatesPath(),
      testId: "brand-settings-nav-candidates",
    },
    {
      // OKS-462 — Integrations (brand's own TikTok account link).
      title: t("settings.brand.nav.integrations"),
      href: brandSettingsIntegrationsPath(),
      testId: "brand-settings-nav-integrations",
    },
    {
      // OKS-931 — Produits, the synced Shopify catalogue. Sits DIRECTLY
      // after Integrations (PRD D1): it is downstream of the connection, and
      // that adjacency makes the connect → catalogue causality legible in the
      // nav order itself. A top-level Brand nav item would advertise a
      // management surface that does not exist until OKS-932.
      title: t("settings.brand.nav.products"),
      href: brandSettingsProductsPath(),
      testId: "brand-settings-nav-products",
    },
    {
      // OKS-672 — Team relocated into Brand Settings as a first-class
      // internal tab (was an external cross-link ↗ to /brand/team).
      // Active-state highlight now resolves against `brandSettingsTeamPath`.
      title: t("settings.shared.nav.team"),
      href: brandSettingsTeamPath(),
      testId: "brand-settings-nav-team",
    },
  ]

  return (
    <>
      <SettingsSubNavMobile
        items={items.map((i) => ({
          label: i.title,
          href: i.href,
          testId: `${i.testId}-mobile`,
        }))}
        currentHref={url}
        ariaLabel={t("settings.brand.nav.section")}
        testId="brand-settings-nav-mobile"
      />
      <nav
        data-testid="brand-settings-nav"
        aria-label={t("settings.brand.nav.section")}
        className="hidden flex-col space-y-1 space-x-0 md:flex"
      >
        {items.map((item) => {
          const active = url === item.href || url.startsWith(`${item.href}?`)
          return (
            <Button
              key={item.href}
              size="sm"
              variant="ghost"
              asChild
              className={cn("w-full justify-start", {
                "bg-muted": active,
              })}
            >
              <Link
                href={item.href}
                data-testid={item.testId}
                aria-current={active ? "page" : undefined}
              >
                {item.title}
                {item.external ? (
                  <span className="text-muted-foreground ml-2 text-xs">↗</span>
                ) : null}
              </Link>
            </Button>
          )
        })}
      </nav>
    </>
  )
}
